import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// ─────────────────────────────────────────────
// Axios instance — auth is fully cookie-based.
// The backend handles access_token + refresh_token rotation transparently.
// Frontend never reads, stores, or manages tokens.
// ─────────────────────────────────────────────
const apiClient = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true, // browser sends HttpOnly cookies automatically on every request
    headers: { 'Content-Type': 'application/json' },
});

// 401 = session expired → redirect to login
// But skip this for auth endpoints themselves (bad credentials, not expired session)
apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        const requestUrl = error.config?.url || '';
        const isAuthEndpoint = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');

        if (error.response?.status === 401 && !isAuthEndpoint) {
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

class AuthService {
    // Get device information
    getDeviceInfo() {
        return {
            screen_width: window.screen?.width,
            screen_height: window.screen?.height,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            language: navigator.language,
        };
    }

    // Register new user — backend sets HttpOnly cookies automatically
    async register(userData) {
        try {
            const response = await apiClient.post('/api/v1/auth/register', {
                username: userData.username,
                email: userData.email,
                password: userData.password,
                company: userData.company,
                phone: userData.phone,
            });
            const { user } = response.data;
            return { user };
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Login — backend sets HttpOnly cookies automatically
    async login(email, password) {
        try {
            const response = await apiClient.post('/api/v1/auth/login', {
                username: email,
                password,
            });
            const { user } = response.data;
            return { user };
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Get current user — backend transparently refreshes cookies if needed
    async getCurrentUser() {
        try {
            const response = await apiClient.get('/api/v1/auth/me');
            return response.data;
        } catch (error) {
            throw this.handleError(error);
        }
    }

    // Logout — backend clears both HttpOnly cookies
    async logout() {
        try {
            await apiClient.post('/api/v1/auth/logout');
        } catch (error) {
            // Ignore errors on logout
        }
    }

    handleError(error) {
        const message = error.response?.data?.detail || error.response?.data?.message || error.message || 'An error occurred';
        return new Error(message);
    }
}

const authService = new AuthService();
export default authService;
